export const SERVER_MESSAGES = {
  /* Common/General Messages */
  COMMON: {
    ROOM_NOT_FOUND: "Room not found",
    MEETING_NOT_FOUND: "Meeting not found",
    ROOM_NAME_REQUIRED: "Room name is required",
    ROOM_ID_REQUIRED: "Room ID is required",
    INTERNAL_SERVER_ERROR: "Internal server error",
    UNAUTHORIZED: "Unauthorized"
  },

  /* Authentication Messages */
  AUTH: {
    INVALID_CREDENTIALS: "Invalid credentials",
    INVALID_PASSWORD: "Invalid password",
    EMAIL_PASSWORD_REQUIRED: "Email and password are required",
    LOGIN_SUCCESS: "Login successfully",
    USER_CREATED: "User created successfully",
    USER_EXISTS: "User already exists",
    FETCH_USER_FAILED: "Failed to fetch user details from Odoo",
    PUBLIC_USER_NOT_ALLOWED: "Public users are not allowed to join meetings"
  },

  /* Meeting Messages */
  MEETING: {
    // Status
    MEETING_ENDED: "This meeting has ended",
    MEETING_CANCELLED: "This meeting has been cancelled",
    MEETING_CREATED: "Room created successfully",
    MEETING_JOINED: "Meeting joined successfully",
    MEETING_FETCHED: "Meeting fetched successfully",
    WAITING_APPROVAL: "Waiting for host approval",
    HOST_NOT_JOINED: "Host has not joined yet.",
    SCHEDULE_INCOMPLETE: "Meeting schedule information is incomplete",
    MEETING_ALREADY_ENDED: "Meeting has already ended.",
    MEETING_NOT_STARTED: (scheduledTime: string) => `Meeting has not started yet. Scheduled for ${scheduledTime}`,

    // Leave
    MEETING_ENDED_FOR_ALL: "Meeting ended for all participants",
    HOST_LEFT_MEETING: "Host left the meeting",
    LEFT_MEETING: "Left the meeting",
    NOT_HOST: "You are not the host of this meeting",

    // Update/Delete
    MEETING_UPDATED: "Meeting updated successfully",
    MEETING_DELETED: "Meeting and all associated data deleted successfully"
  },

  /* Participant Messages */
  PARTICIPANT: {
    // Approval/Rejection
    USER_APPROVED: "User approved successfully",
    USER_REJECTED: "User rejected successfully",
    GUEST_APPROVED: "Guest approved successfully",
    GUEST_REJECTED: "Guest rejected successfully",
    FAILED_APPROVE_GUEST: "Failed to approve guest",
    USER_NOT_REQUESTED: "User has not requested to join this meeting",
    GUEST_NOT_REQUESTED: "Guest has not requested to join this meeting",
    HOST_REJECTED: "Host has rejected your request to join this meeting",
    HOST_APPROVED: "Host has approved your request",

    // Management
    PARTICIPANTS_ADDED: "Participants added successfully",
    PARTICIPANT_REMOVED: "Participant removed successfully",
    PARTICIPANTS_ADDED_TO_INVITED: "Participants added to invitedUsers list successfully",
    NO_USERS_FOUND: "No users found",

    // Mute/Unmute
    MUTE_REQUEST_SENT: (roomName: string, identity: string, trackType: string) =>
      `Participant ${identity} in ${roomName} - ${trackType} muted successfully`,
    UNMUTE_REQUEST_SENT: (identity: string, trackType: string) => `Unmute request sent to ${identity} for ${trackType}`,
    UNMUTE_REQUEST_FAILED: (trackType: string) => `Failed to send unmute request for ${trackType}`,
    NO_TRACK_FOUND: (trackType: string) => `No ${trackType} track found for participant`,
    PARTICIPANT_KICKED: (identity: string) => `Participant ${identity} removed successfully`,
    PARTICIPANTS_FETCHED: "Participants fetched successfully",
    FILTERED_PARTICIPANTS_FETCHED: "Filtered participants fetched successfully",
    ALL_MUTED: (count: number) => `All ${count} participants muted successfully`,
    ALL_VIDEOS_STOPPED: (count: number) =>
      count === 0 ? "No participant videos to turn off" : `Turned off video for ${count} participant(s)`,
    ALL_SCREEN_SHARES_STOPPED: (count: number) =>
      count === 0 ? "No active screen shares to stop" : `Stopped ${count} screen share(s)`,
    LIVE_CURSORS_UPDATED: (disabled: boolean) =>
      disabled ? "Live cursors disabled for canvas and notes" : "Live cursors enabled for canvas and notes"
  },

  /* Chat Messages */
  CHAT: {
    MESSAGE_SAVED: "Message saved successfully",
    CHAT_FETCHED: "Chat history fetched successfully"
  },

  /* Poll Messages */
  POLL: {
    CREATED: "Poll created successfully",
    CLOSED: "Poll closed successfully",
    VOTE_SUBMITTED: "Vote submitted successfully",
    NOT_FOUND: "Poll not found",
    FETCHED: "Poll data fetched successfully"
  },

  /* Recording Messages */
  RECORDING: {
    // Start
    START_SUCCESS: "Recording started successfully",
    ALREADY_RECORDING: "Meeting is already being recorded.",
    ALREADY_RECORDING_OTHER: "Meeting is already being recorded by another process.",
    MEETING_NOT_ACTIVE: "Room not found or is not active. Please ensure the meeting is in progress.",
    MEETING_VERIFY_FAILED: "Meeting not found. Please verify the room name and host ID.",
    DB_SAVE_FAILED: "Failed to save recording information. Recording has been stopped.",

    // Stop
    STOP_SUCCESS: "Recording stop request processed.",
    NOT_FOUND_DB: "Recording not found in database.",
    ALREADY_STOPPED: "Recording has already been stopped.",
    RECORDING_FAILED: "Recording has failed and cannot be stopped.",
    NO_PERMISSION: "Meeting not found or you don't have permission to stop recordings.",
    NO_ACTIVE_RECORDINGS: "No active recordings found for this room.",
    ALREADY_STOPPED_LIVEKIT: "Recordings were already stopped on LiveKit. Database updated.",
    NOT_FOUND_STOPPED: "Recording not found or already stopped.",
    COMPLETED_STOPPED: "Recording has already been stopped or completed.",

    // Status
    NO_ACTIVE_RECORDING: "No active recording",
    ACTIVE_RECORDING_FOUND: "Active recording found",
    NO_RECORDINGS_FOUND: "No recordings found",
    FETCHED: "recordings fetched successfully",

    // Errors
    LIVEKIT_CONNECTION_FAILED: "Unable to connect to LiveKit server. Please check server status and try again.",
    LIVEKIT_AUTH_FAILED: "LiveKit authentication failed. Please check API credentials.",
    EGRESS_LIMIT: "Recording limit reached. Please stop other recordings first.",
    SERVICE_UNAVAILABLE: "Recording service temporarily unavailable. Please try again later.",
    ROOM_NOT_ACTIVE: "Room not found. Meeting may have ended."
  },

  /* Admin Messages */
  ADMIN: {
    STATS_FETCHED: "Statistics fetched successfully",
    EXPORT_SUCCESS: "Meetings exported successfully",
    INVALID_FORMAT: "Invalid format. Use 'json' or 'csv'",
    MEETINGS_FETCHED: "Meetings fetched successfully"
  },

  /* Toast Messages */
  TOAST: {
    SUCCESS: {
      WELCOME_BACK: "Welcome back! Joining meeting..."
    }
  },

  /* Validation Messages */
  VALIDATION: {
    MISSING_REQUIRED_FIELD: "Missing required field",
    INVALID_PARAM: "Invalid parameter"
  }
} as const;

/* Type exports for type-safe usage */
export type ServerMessages = typeof SERVER_MESSAGES;
export type CommonMessages = typeof SERVER_MESSAGES.COMMON;
export type AuthMessages = typeof SERVER_MESSAGES.AUTH;
export type MeetingServerMessages = typeof SERVER_MESSAGES.MEETING;
export type ParticipantServerMessages = typeof SERVER_MESSAGES.PARTICIPANT;
export type ChatServerMessages = typeof SERVER_MESSAGES.CHAT;
export type PollServerMessages = typeof SERVER_MESSAGES.POLL;
export type RecordingMessages = typeof SERVER_MESSAGES.RECORDING;
export type AdminMessages = typeof SERVER_MESSAGES.ADMIN;
export type ToastServerMessages = typeof SERVER_MESSAGES.TOAST;
export type ValidationServerMessages = typeof SERVER_MESSAGES.VALIDATION;
